import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, MonitorPlay } from 'lucide-react';
import type { ApiError } from '../../lib/api';
import { sesionApi, sesionKeys, type ActividadDetalle } from '../../lib/sesion';

/**
 * SWITCH DE PROYECCIÓN — qué está mostrando la Pantalla ahora mismo.
 *
 * El facilitador alterna entre EXPLICAR (la consigna completa de la
 * herramienta, sin aportes: el momento de dar la instrucción a la sala) y
 * TABLERO (el formato con las participaciones en vivo: el momento de
 * discutir). «Automático» deja que la Pantalla decida sola: explica mientras
 * no hay aportes y pasa al tablero desde el primero. Viaja por el WS de la
 * sala en < 1 s; la Pantalla sigue sin controles propios.
 */
export function ProyeccionControl({
  actividad,
  puedeConducir,
}: {
  actividad: ActividadDetalle;
  puedeConducir: boolean;
}) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (modo: 'explicar' | 'tablero' | null) =>
      sesionApi.marcarProyeccion(actividad.id, modo),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sesionKeys.actividad(actividad.id) });
    },
  });

  const enCurso = actividad.estado === 'abierta' || actividad.estado === 'pausada';
  if (!enCurso || !puedeConducir) return null;

  const activo = actividad.proyeccion_modo ?? null;
  const error = (mut.error as ApiError | null)?.detail ?? null;

  const opciones: Array<{ valor: 'explicar' | 'tablero' | null; etiqueta: string }> = [
    { valor: null, etiqueta: 'Automático' },
    { valor: 'explicar', etiqueta: 'Explicar' },
    { valor: 'tablero', etiqueta: 'Tablero' },
  ];

  return (
    <section className="rounded-md border border-border-strong bg-surface-raised px-4 py-3">
      <p className="flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-content">
        <MonitorPlay className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        Pantalla del salón
      </p>
      <p className="mt-1 font-sans text-xs text-content-muted">
        «Explicar» proyecta la consigna completa para dar la instrucción;
        «Tablero» proyecta el formato con los aportes para discutirlos. En
        automático, la Pantalla explica hasta que llega el primer aporte.
      </p>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {opciones.map((o) => (
          <button
            key={o.etiqueta}
            type="button"
            disabled={mut.isPending}
            onClick={() => mut.mutate(o.valor)}
            aria-pressed={activo === o.valor}
            className={`min-h-touch rounded-md border px-3 font-sans text-sm font-medium transition-colors duration-fast focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-50 ${
              activo === o.valor
                ? 'border-primary bg-surface-sunken text-content'
                : 'border-border-strong bg-surface-raised text-content-muted hover:border-primary'
            }`}
          >
            {o.etiqueta}
          </button>
        ))}
      </div>

      {error && (
        <p className="mt-2 flex items-center gap-1.5 font-sans text-sm text-error">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}
    </section>
  );
}
